import {Component, EventEmitter, forwardRef, Input, OnInit, Output, AfterViewInit} from '@angular/core';
import {UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ApiService} from '../../shared/services/api.service';
import {alertMessages, AlertService} from '../../shared/services/alert.service';
import {empty} from '../../functions';
import {catchError, debounceTime} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {faSpinner} from '@fortawesome/free-solid-svg-icons';
import {DomainService} from '../../shared/services/domain.service';

const noop = () => {
};

export const TYPEAHEAD_EDUCATIONPROGRAM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TypeaheadEducationprogramComponent),
  multi: true
};

@Component({
    selector: '[app-typeahead-educationprogram]',
    templateUrl: './typeahead-educationprogram.component.html',
    styleUrls: ['./typeahead-educationprogram.component.scss'],
    providers: [TYPEAHEAD_EDUCATIONPROGRAM_INPUT_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class TypeaheadEducationprogramComponent implements OnInit {

  debounceTime = 500;

  // Icons
  faSpinner = faSpinner;

  searchControl: UntypedFormControl;

  useEducationData = true;

  /**
   * Inputs
   */
  @Input() selectorName = 'title';
  @Input() selectorValue = 'title';

  @Input() label = '';

  @Input() apiEndpoint = 'educationprogram';
  @Input() data: any[] = [];
  @Input() hasPagination = true;
  @Input() pageSize = 20;

  // Emitter
  @Output() selectEvent: EventEmitter<any> = new EventEmitter<any>();

  showDropdown = false;
  isLoading = false;

  /**
   * Ng typeahead properties
   */

  public model: any;

  /**
   * Control accessors
   */

    // The internal data model
  private innerValue: any = '';

  // Placeholders for the callbacks which are later provided
  // by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  /**
   * Constructor
   *
   * @param api
   * @param alertService
   */
  constructor(private api: ApiService,
              private alertService: AlertService,
              private domain: DomainService
              ) {


    this.searchControl = new UntypedFormControl();

    // Get education to show when input changes
    this.searchControl.valueChanges.pipe(debounceTime(this.debounceTime)).subscribe((result) => {

      this.value = result;

      // Reload data if input value changed
      if (!empty(this.value) && this.useEducationData) {
        this.getEducationData();
      }

    });

  }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    this.useEducationData = this.domain.useEducationData();

  }

  /**
   * Close dropdown
   *
   * @param $timeout
   */
  closeDropdown($timeout: number = 0) {
    setTimeout(() => {
      this.showDropdown = false;
    }, $timeout);
  }

  /**
   * Get data
   */
  getEducationData() {

    if (empty(this.apiEndpoint)) return;
    if (this.isLoading) return;

    this.isLoading = true;

    let queryParams = null;

    if (this.hasPagination) {
      queryParams = {
        PageNumber: 1,
        PageSize: this.pageSize,
        SearchTerm: this.innerValue
      };
    }

    this.api.setEndpoint(this.apiEndpoint);

    this.api.GET(queryParams).pipe(
      catchError(this.handleError('apiGET', false))
    ).subscribe((result: any) => {

      if (result) {

        const collection: any[] = [];

        // Create custom typeahead collection of requested data
        if (result.hasOwnProperty('data')) {

          for (const i in result.data) {
            const row = result.data[i];

            const newRow = {
              value: row[this.selectorValue],
              name: row[this.selectorName],
              suffix: this.formatRowSuffix(row),
              data: row
            }

            collection.push(newRow);
          }

        }

        this.data = Object.assign([], collection);
        this.isLoading = false;
        this.showDropdown = true;

      } else {
        this.isLoading = false;
        this.showDropdown = false;
      }

    });

  }

  /**
   * Format data for the suffix in dropdown menu
   *
   * @param $row
   */
  formatRowSuffix($row: any) {
    let result = '';
    if ($row && $row.hasOwnProperty('school')) {
      let suffixParams = [];

      if (!empty($row['school']['name'])) {
        suffixParams.push($row['school']['name']);
      }

      if (!empty($row['school']['address']) && !empty($row['school']['address']['city'])) {
        suffixParams.push($row['school']['address']['city']);
      }

      if (suffixParams.length > 0) {
        result = suffixParams.join(', ');
      }
    }

    return result;
  }

  /**
   * On select event handler
   *
   * @param $event
   */
  onSelect($event: any) {

    // Set value in input
    if ($event.hasOwnProperty('data')) {
      if ($event['data'].hasOwnProperty(this.selectorValue)) {
        this.value = $event['data'][this.selectorValue];
        this.searchControl.setValue($event['data'][this.selectorName], {emitEvent: false});
      }
    }

    this.selectEvent.emit($event);
    this.showDropdown = false;
  }

  // get accessor
  get value(): any {
    return this.innerValue;
  };

  // set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    this.innerValue = value;
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }


  /**
   * Error handler
   *
   * @param operation
   * @param result
   */
  handleError<T>(operation = 'operation', result?: T) {

    return (error: any): Observable<T> => {

      // Handle given operation
      switch (operation) {

        case 'apiGET':
          // do something
          break;

        default:
          this.alertService.errorWithReport(error, `typeaheadEducationprogram:${operation}`);
          break;

      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

}
